import { useQuery } from '@tanstack/react-query';
import { AxiosResponse, AxiosResponseHeaders } from 'axios';
import { API } from 'network/api';
import { useMemo } from 'react';
import { CommonListParams, PaddyBuyer } from 'types/common';
import { cleanObject } from 'utils/helpers';

type PaddyBuyerResponse = AxiosResponse<PaddyBuyer[]>;

type PaddyBuyerParams = CommonListParams & {
  country?: string;
  agronomist?: string;
  subDistrict?: string;
  status?: string;
  srAg?: string;
};

const usePaddyBuyerList = (params: PaddyBuyerParams, enabled = true) => {
  const {
    country,
    search,
    agronomist,
    subDistrict,
    sort,
    status,
    srAg,
    ...restParams
  } = {
    ...params,
  };
  const queryParams = useMemo(
    () =>
      cleanObject({
        'name.contains': search,
        'country.equals': country,
        'agName.in': agronomist,
        'subDistrict.in': subDistrict,
        'status.in': status,
        'seniorAgEmail.in': srAg,
        ...restParams,
      }),
    [params],
  );

  const query = useQuery<PaddyBuyerResponse>({
    queryKey: ['paddy-buyer', queryParams],
    queryFn: () => API.get('/paddy-buyers', { params: queryParams }),
    enabled: enabled,
    staleTime: 1000 * 10,
  });

  const paddyBuyerOptions = useMemo(() => {
    return query.data?.data?.map((data) => ({
      value: data.id,
      label: data.name,
      status: data?.status,
    }));
  }, [query.data]);

  const headers = query?.data?.headers as AxiosResponseHeaders;
  const totalCount = Number(headers?.get('X-Total-Count')) || 0;

  return { ...query, paddyBuyerOptions, totalCount };
};

export default usePaddyBuyerList;
